// Section 22 Society tab (Stream D), the tab root: reads the store's signals, builds the view
// model and dispatches the society actions. Settlement trust and happiness with their breakdowns,
// the inequality dial, norms in force and faction marks, the festival, pairings awaiting approval,
// couples, the justice slot (M6 mounts Stream E1's JusticePanel via `justice`), the Ledger link,
// and the education panel. Every other component in this folder takes props.
import type { ComponentChildren } from 'preact';
import { useComputed } from '@preact/signals';
import {
  answerPairing,
  draft,
  game,
  openOverlay,
  runConfig,
  setApprenticeship,
  setSchoolSubjects,
  setSchooling,
  status,
} from '../../store';
import { jobLabel } from '../../viewModels/people';
import { societyVM } from '../../viewModels/society';
import { societyText } from '../text/en';
import { EducationPanel } from './education-panel';
import { FactorTip } from './factor-tip';
import { CouplesPanel, FestivalPanel, PairingsPanel } from './family-panel';
import { InequalityDial } from './inequality-dial';
import { NormsPanel } from './norms-panel';
import './society.css';

interface Props {
  /** Stream E1's JusticePanel, mounted here by the integration (M6). */
  justice?: ComponentChildren;
}

export function SocietyTab({ justice }: Props) {
  const model = useComputed(() =>
    game.value === null ? null : societyVM(game.value, draft.value, runConfig()),
  );
  const vm = model.value;
  if (vm === null) return null;
  const canEdit = status.value === 'deciding';
  const t = vm.thresholds;
  return (
    <div class="society">
      <section class="society__section" aria-labelledby="society-mood-title">
        <h3 id="society-mood-title">{societyText.title}</h3>
        <dl class="society-stats">
          <dt>{societyText.trust}</dt>
          <dd>
            <FactorTip
              value={vm.trust.value.toFixed(0)}
              tooltip={vm.trust.tooltip}
              label={societyText.trust}
            />
          </dd>
          <dt>{societyText.happiness}</dt>
          <dd>
            <FactorTip
              value={vm.happiness.value.toFixed(0)}
              tooltip={vm.happiness.tooltip}
              label={societyText.happiness}
            />
          </dd>
        </dl>
        <p class="society-muted">{societyText.trustHint(t.refusal, t.crime, t.bonus)}</p>
        {vm.refusingHouseholds > 0 && (
          <p class="society-warn" role="status">
            {societyText.refusal(vm.refusingHouseholds)}
          </p>
        )}
        {vm.uncoveredChildren > 0 && (
          <p class="society-warn">{societyText.uncovered(vm.uncoveredChildren)}</p>
        )}
        <button type="button" class="button" onClick={() => openOverlay('ledger')}>
          {societyText.ledger}
        </button>
      </section>

      <section class="society__section" aria-labelledby="society-inequality-title">
        <h3 id="society-inequality-title">{societyText.inequality}</h3>
        <InequalityDial
          food={vm.inequality.food}
          beds={vm.inequality.beds}
          series={vm.inequality.series}
        />
      </section>

      <section class="society__section" aria-labelledby="society-norms-title">
        <h3 id="society-norms-title">{societyText.norms}</h3>
        <NormsPanel norms={vm.norms} factions={vm.factions} />
      </section>

      <section class="society__section" aria-labelledby="society-festival-title">
        <h3 id="society-festival-title">{societyText.festival}</h3>
        <FestivalPanel festival={vm.festival} />
      </section>

      <section class="society__section" aria-labelledby="society-pairings-title">
        <h3 id="society-pairings-title">{societyText.pairings}</h3>
        <PairingsPanel
          rows={vm.pairings}
          canEdit={canEdit}
          jobLabel={jobLabel}
          onAnswer={(id, approve) => answerPairing(id, approve)}
        />
        <h4>{societyText.couples}</h4>
        <CouplesPanel rows={vm.couples} />
      </section>

      <section class="society__section" aria-labelledby="society-justice-title">
        <h3 id="society-justice-title">{societyText.justice}</h3>
        {justice ?? <p class="society-muted">{societyText.justiceLater}</p>}
      </section>

      <section class="society__section" aria-labelledby="society-education-title">
        <h3 id="society-education-title">{societyText.education}</h3>
        <EducationPanel
          education={vm.education}
          apprenticeships={vm.apprenticeships}
          masters={vm.masters}
          apprentices={vm.apprentices}
          canEdit={canEdit}
          jobLabel={jobLabel}
          onSchooling={(o) => setSchooling(o)}
          onSubjects={(a, b) => setSchoolSubjects(a, b)}
          onApprentice={(a, m) => setApprenticeship(a, m)}
        />
      </section>
    </div>
  );
}
